import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import * as THREE from 'three';
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { CustomGridHelper } from "components/Viewer3d/GridHelper";
import { Text } from 'troika-three-text';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter';
import { LoopSubdivision } from 'three-subdivide';

export const LoadStl = async (filePath: string): Promise<THREE.BufferGeometry | null> => {
  const stlLoader = new STLLoader();

  try {
    const response = await fetch(filePath);
    if (!response.ok) {
      console.error(`Failed to fetch STL file: ${filePath} (HTTP ${response.status})`);
      return null;
    }

    const buffer = await response.arrayBuffer();
    const geometry = stlLoader.parse(buffer);
    // console.log(`loaded stl file: ${geometry.attributes.position.array.length}`)

    // Optional sanity check
    if (!geometry || !geometry.attributes?.position) {
      console.error(`STL file ${filePath} is malformed.`);
      return null;
    }

    return geometry;
  } catch (error) {
    console.error('Error loading STL file:', error);
    return null;
  }
};

export const loadFromArrayBuffer = (arrayBuffer: ArrayBuffer): THREE.BufferGeometry | null => {
  const stlLoader = new STLLoader();
  try {
    const geometry = stlLoader.parse(arrayBuffer);
    // console.log(`array buffer loaded: ${geometry.attributes.position.array.length}`);
    if (!geometry || !geometry.attributes?.position) {
      console.error(`array buffer is malformed.`);
      return null;
    }
    const validated = validateGeometry(geometry);
    if (!validated) return null;

    return geometry;
  } catch (error) {
    return null;
  }
}

// Function to clear duplicate vertices for BufferGeometry
export const clearDuplicateVertices = (geometry: THREE.BufferGeometry) => {
  const positionAttribute = geometry.getAttribute('position');
  const positions = positionAttribute.array;
  const vertexCount = positionAttribute.count;

  // Store unique vertices and mapping
  const uniqueVertices: THREE.Vector3[] = [];
  const vertexMap: number[] = [];
  const newIndices: number[] = [];

  // Find unique vertices
  for (let i = 0; i < vertexCount; i++) {
    const vertex = new THREE.Vector3(
      positions[i * 3],
      positions[i * 3 + 1],
      positions[i * 3 + 2]
    );

    let isDuplicate = false;
    let duplicateIndex = -1;

    // Check if this vertex already exists
    for (let j = 0; j < uniqueVertices.length; j++) {
      if (vertex.equals(uniqueVertices[j])) {
        isDuplicate = true;
        duplicateIndex = j;
        break;
      }
    }

    if (isDuplicate) {
      vertexMap[i] = duplicateIndex;
    } else {
      vertexMap[i] = uniqueVertices.length;
      uniqueVertices.push(vertex.clone());
    }
  }

  // Create new position array for unique vertices
  const newPositions = new Float32Array(uniqueVertices.length * 3);
  uniqueVertices.forEach((vertex, i) => {
    newPositions[i * 3] = vertex.x;
    newPositions[i * 3 + 1] = vertex.y;
    newPositions[i * 3 + 2] = vertex.z;
  });

  // Create new indices array
  if (geometry.index) {
    const indices = geometry.index.array;
    for (let i = 0; i < indices.length; i++) {
      newIndices.push(vertexMap[indices[i]]);
    }
  } else {
    for (let i = 0; i < vertexCount; i++) {
      newIndices.push(vertexMap[i]);
    }
  }

  // Create new geometry with unique vertices
  const newGeometry = new THREE.BufferGeometry();
  newGeometry.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));
  newGeometry.setIndex(newIndices);
  newGeometry.computeVertexNormals();

  // Validate the new geometry
  if (newPositions.length === 0 || newIndices.length === 0) {
    console.error("clearDuplicateVertices: Generated geometry is empty");
    return geometry; // Return original geometry if new one is invalid
  }

  return newGeometry;
};

export const validateGeometry = (geometry: THREE.BufferGeometry): boolean => {
  if (!geometry?.attributes?.position)
    return false;

  const pos = geometry.attributes.position.array as Float32Array;

  for (let i = 0; i < pos.length; i++) {
    if (!Number.isFinite(pos[i])) {
      console.error(`Invalid position value at index ${i}:`, pos[i]);
      return false;
    }
  }
  if (pos.length % 3 !== 0) {
    console.error(`Position array length ${pos.length} is not multiple of 3`);
    return false;
  }

  const box = new THREE.Box3().setFromBufferAttribute(geometry.attributes.position as THREE.BufferAttribute);
  const size = new THREE.Vector3();
  box.getSize(size);
  if (size.lengthSq() === 0) {
    console.error("Geometry has zero volume (collapsed bounding box).");
    return false;
  }

  return true;
};

/**
 * Creates a new mesh from the position attributes of another mesh
 * @param sourceMesh - The mesh to clone
 * @param material - The material to use for the new mesh (default: null)
 * @returns A new THREE.Mesh with the same vertex positions
 */
export function cloneMeshWithTransform(sourceMesh: THREE.Mesh, material: THREE.Material | null = null): THREE.Mesh {
  let newMaterial: THREE.Material = material || (Array.isArray(sourceMesh.material) ? sourceMesh.material[0] : sourceMesh.material);
  const clonedMaterial = newMaterial.clone();

  const newMesh = new THREE.Mesh(sourceMesh.geometry.clone(), clonedMaterial);

  // Copy the transformation
  newMesh.matrixWorld.copy(sourceMesh.matrixWorld);
  newMesh.applyMatrix4(sourceMesh.matrixWorld);

  return newMesh;
}

export function cloneMesh(source: THREE.Mesh): THREE.Mesh {
  const material = Array.isArray(source.material)
    ? source.material.map(m => m.clone())
    : source.material.clone();

  const cloned = new THREE.Mesh(source.geometry.clone(), material);
  cloned.position.copy(source.position);
  cloned.quaternion.copy(source.quaternion);
  cloned.scale.copy(source.scale);
  cloned.name = source.name;
  cloned.userData = { ...source.userData };
  return cloned;
}

/**
 * Scales an object around a specific point in 3D space
 * @param object - The mesh/object to scale
 * @param point - The point around which to scale (THREE.Vector3)
 * @param x - Scale factor for x-axis
 * @param y - Scale factor for y-axis
 * @param z - Scale factor for z-axis
 */
export const scaleAroundPoint = (
  object: THREE.Object3D,
  point: THREE.Vector3,
  x: number,
  y: number,
  z: number
): void => {
  // Store original position
  const originalPosition = object.position.clone();

  // Move to origin relative to scaling point
  object.position.sub(point);

  // Apply scale
  object.scale.set(x, y, z);

  // Move back to original position
  object.position.add(point);
};

/**
 * Creates a line shape in the XZ plane from boundary points
 * @param points Array of points defining the boundary of the line shape in the XZ plane
 * @param color Color of the line (default: 0x000000)
 * @param lineWidth Width of the line (default: 1)
 * @returns A THREE.Line representing the shape
 */
export function createLineShapeFromPoints(points: THREE.Vector3[], color: number = 0x000000, lineWidth: number = 1): LineSegments2 {
  // Create a geometry from the points
  const positions = new Float32Array(points.length * 3);
  for (let i = 0; i < points.length; i++) {
    positions[i * 3] = points[i].x;
    positions[i * 3 + 1] = points[i].y;
    positions[i * 3 + 2] = points[i].z;
  }

  const geometry = new LineGeometry();
  geometry.setPositions(positions);

  // Create material
  const material = new LineMaterial({
    color: color,
    linewidth: lineWidth, // in pixels
    resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
    wireframe: true,
  });

  // Create and return the line
  const line = new LineSegments2(geometry, material);
  line.computeLineDistances();

  line.scale.set(1, 1, 1);

  return line;
}

export function createLineGeoFromPoints(points: THREE.Vector3[], color: number = 0xffffff, lineWidth: number = 5): THREE.Line {
  const line = new THREE.CatmullRomCurve3(points, false);
  const linePoints = line.getPoints(10);
  const geometry = new THREE.BufferGeometry().setFromPoints(linePoints);
  const material = new THREE.LineBasicMaterial({
    color: 0x07d2da,
    linewidth: lineWidth,
    depthTest: false,
    depthWrite: false,
    transparent: true,
    opacity: 1.0
  });
  const lineObj = new THREE.Line(geometry, material);
  return lineObj

}

export const fitCameraToSceneObjects = (
  camera: THREE.Camera,
  controls: OrbitControls,
  container: HTMLDivElement,
  sceneRef: any,
  mode: 'load' | 'toggle' = 'toggle'
) => {
  if (!sceneRef.current || !camera || !controls || !container) return;

  const aspect = container.clientWidth / container.clientHeight;
  const loadDirection = new THREE.Vector3(0, 10, 5).normalize();

  // OrbitControls keeps damped rotation and pan deltas internally. Flush them
  // before a full load reset so the next update cannot reapply the previous
  // user view to the newly positioned camera.
  if (mode === 'load') {
    const dampingEnabled = controls.enableDamping;
    controls.enableDamping = false;
    controls.update();
    controls.enableDamping = dampingEnabled;
    camera.up.set(0, 1, 0);

    if (camera instanceof THREE.PerspectiveCamera) {
      camera.fov = 60;
      camera.aspect = aspect;
    }
  }

  // -------------------------------
  // STEP 1: Compute the bounding box of all visible meshes
  // -------------------------------
  sceneRef.current.updateMatrixWorld(true);

  const box = calculateBoundingBoxOfAllMeshes(sceneRef.current);
  if (box.isEmpty()) return;

  // const tempBox = new THREE.Box3();
  // let found = false;

  // sceneRef.current.traverse((obj: THREE.Object3D) => {
  //   if (!(obj instanceof THREE.Mesh) || !obj.visible || !obj.geometry) return;
  //   if (!obj.geometry.boundingBox) {
  //     obj.geometry.computeBoundingBox();
  //   }

  //   tempBox.copy(obj.geometry.boundingBox!);
  //   tempBox.applyMatrix4(obj.matrixWorld);

  //   box.union(tempBox);
  //   found = true;
  // });

  // if (!found) return;
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  const padding = box.max.x > 150 ? 1 : 1.4;

  // -------------------------------
  // STEP 2: Auto-fit camera to the model for large objects
  // -------------------------------
  if (camera instanceof THREE.PerspectiveCamera) {
    let dir = mode === 'load'
      ? loadDirection.clone()
      : camera.position.clone().sub(controls.target);
    if (dir.length() < 0.001) dir.set(0, 0, 1);
    dir.normalize();

    const fov = (camera.fov * Math.PI) / 180;
    const fitHeightDistance = (maxDim * padding * 0.5) / Math.tan(fov / 2);

    const fitWidthDistance = fitHeightDistance / aspect;

    const distance = Math.max(fitHeightDistance, fitWidthDistance);

    camera.position.copy(center.clone().add(dir.multiplyScalar(distance)));
    camera.lookAt(center);
    camera.near = distance / 100;
    camera.far = Math.max(distance * 100, 1000);
    camera.updateProjectionMatrix();
  } else if (camera instanceof THREE.OrthographicCamera) {
    const height = maxDim * padding;
    const width = height * aspect;

    camera.left = -width / 2;
    camera.right = width / 2;
    camera.top = height / 2;
    camera.bottom = -height / 2;

    // Increase distance to prevent camera from being too close
    let dir = mode === 'load'
      ? loadDirection.clone()
      : camera.position.clone().sub(controls.target);
    if (dir.length() < 0.001) dir.set(0, 0, 1);
    dir.normalize();

    const distance = maxDim * 2;
    camera.position.copy(center.clone().add(dir.multiplyScalar(distance)));
    camera.lookAt(center);

    camera.near = distance / 10;
    camera.far = Math.max(distance * 100, 1000);
    camera.updateProjectionMatrix();
  }

  // -------------------------------
  // STEP 3: Update controls and camera world matrix
  // -------------------------------
  controls.target.copy(center);
  if (mode === 'load') {
    const dampingEnabled = controls.enableDamping;
    controls.enableDamping = false;
    controls.update();
    controls.enableDamping = dampingEnabled;
  } else {
    controls.update();
  }
  camera.updateMatrixWorld(true);
};

export const setColorBufferBySelection = (bufferGm: THREE.BufferGeometry, bottomColor: THREE.Color, topColor: THREE.Color) => {
  const positionAttribute = bufferGm.getAttribute('position');
  const selectedAttribute = bufferGm.getAttribute('selected');
  const colorBuffer = new Float32Array(positionAttribute.count * 3);
  if (!selectedAttribute) return null;

  const selectedArray = selectedAttribute.array;
  for (let i = 0; i < positionAttribute.count; i++) {
    // Get the selected value (0 to 1)
    const selectedValue = selectedArray[i];
    if (selectedValue > 0.9) {
      colorBuffer[i * 3] = topColor.r;     // R - interpolate from blue to red
      colorBuffer[i * 3 + 1] = topColor.g;           // G - fade from blue to 0
      colorBuffer[i * 3 + 2] = topColor.b;           // B - fade from blue to 0
    } else {
      // Non-selected vertices keep the original color
      colorBuffer[i * 3] = bottomColor.r;     // R (0x156289)
      colorBuffer[i * 3 + 1] = bottomColor.g; // G
      colorBuffer[i * 3 + 2] = bottomColor.b; // B
    }
  }
  const colorAttribute = new THREE.BufferAttribute(colorBuffer, 3);
  colorAttribute.name = 'color';
  bufferGm.setAttribute('color', colorAttribute);
}

// Function to create a custom attribute for selected vertices
export const createSelectedVerticesAttribute = (geometry: THREE.BufferGeometry, vertexCount: number): THREE.BufferAttribute => {
  // Create a buffer for the selected vertices attribute (0 = not selected, 1 = selected)
  const selectedBuffer = new Float32Array(vertexCount);
  // Initialize all vertices as not selected (0)
  for (let i = 0; i < vertexCount; i++) {
    selectedBuffer[i] = 0;
  }

  // Create the attribute
  const selectedAttribute = new THREE.BufferAttribute(selectedBuffer, 1);
  selectedAttribute.name = 'selected';

  // Add the attribute to the geometry
  geometry.setAttribute('selected', selectedAttribute);

  //console.log('Created selected attribute with length:', selectedBuffer.length);
  //console.log('First 10 values in selected array:', Array.from(selectedBuffer.slice(0, 10)));

  return selectedAttribute;
};

// Function to count selected vertices in a geometry
export const countSelectedVertices = (geometry: THREE.BufferGeometry): number => {
  const selectedAttribute = geometry.getAttribute('selected');
  if (!selectedAttribute) {
    console.log('No selected attribute found in geometry');
    return 0;
  }

  let count = 0;
  const selectedArray = selectedAttribute.array as Float32Array;

  console.log('Selected attribute array length:', selectedArray.length);
  console.log('First 10 values in selected array:', Array.from(selectedArray.slice(0, 10)));

  for (let i = 0; i < selectedArray.length; i++) {
    if (selectedArray[i] > 0) {
      count++;
    }
  }

  return count;
};

// Function to update the selected vertices attribute based on selected faces
export const updateSelectedVerticesAttribute = (geometry: THREE.BufferGeometry, selectedFaces: Array<{
  faceIndex: number;
  vertexIds: number[];
}>): void => {
  // Log the selected faces for debugging
  console.log('Selected faces:', selectedFaces);
  console.log('Number of selected faces:', selectedFaces.length);

  // Get the selected attribute
  const selectedAttribute = geometry.getAttribute('selected');
  if (!selectedAttribute) {
    console.log('Selected attribute not found, this should not happen');
    return;
  }

  // First, set all vertices to not selected (0)
  for (let i = 0; i < selectedAttribute.array.length; i++) {
    selectedAttribute.array[i] = 0;
  }

  // Then mark vertices from selected faces as selected
  selectedFaces.forEach(face => {
    console.log(`Processing face ${face.faceIndex} with vertex IDs:`, face.vertexIds);
    face.vertexIds.forEach(vertexId => {
      if (vertexId < selectedAttribute.array.length) {
        selectedAttribute.array[vertexId] = 1; // Set to 1 for selected vertices
      }
    });
  });

  // Log the first 10 values after updating
  console.log('First 10 values in selected array after update:', Array.from(selectedAttribute.array.slice(0, 10)));

  // Mark the attribute as needing update
  selectedAttribute.needsUpdate = true;

  // Force the geometry to update
  geometry.attributes.position.needsUpdate = true;
  geometry.computeVertexNormals();
};

export const calculateBoundingBoxOfAllMeshes = (scene: THREE.Scene): THREE.Box3 => {
  // Calculate bounding box of all visible objects
  const box = new THREE.Box3();
  scene.traverse(obj => {
    if (obj.visible) {
      const meshBox = new THREE.Box3().setFromObject(obj);
      if (!meshBox.isEmpty()) {
        box.union(meshBox);
      }
    }
  });
  return box;
}

export const removeExistingGridHelpers = (sceneRef: any) => {
  // Remove existing grid helpers and labelsif present
  if (sceneRef == null) return;
  if (sceneRef) {
    const gridhelpers = sceneRef.children.filter((obj: any) => (obj instanceof CustomGridHelper || obj instanceof Text));
    if (gridhelpers.length <= 0) return;
    gridhelpers.forEach((obj: any) => {
      sceneRef.remove(obj);
    });
  }
}

export const exportSTL = (mesh: THREE.Mesh | null, name: string) => {
  if (!mesh) return;
  const exporter = new STLExporter();
  const result = exporter.parse(mesh, { binary: true }) as unknown as ArrayBuffer;
  const blob = new Blob([result], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name}.stl`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Checks if a mesh's normals are inverted (pointing inwards).
 * @param {THREE.Mesh} mesh The mesh to check.
 * @returns {boolean} True if a significant number of normals are inverted, false otherwise.
 */
export const areNormalsInverted = (geometry: THREE.BufferGeometry) => {
  geometry.computeBoundingBox();

  if (!geometry.boundingBox) {
    console.error("Bounding box is required.");
    return false;
  }

  const bboxCenter = new THREE.Vector3();
  geometry.boundingBox.getCenter(bboxCenter);

  const normal = new THREE.Vector3();
  const vertex = new THREE.Vector3();

  let invertedCount = 0;
  const positionAttribute = geometry.attributes.position;
  const normalAttribute = geometry.attributes.normal;

  for (let i = 0; i < positionAttribute.count; i++) {
    normal.fromBufferAttribute(normalAttribute, i);
    vertex.fromBufferAttribute(positionAttribute, i);

    // Vector pointing from the face (vertex) to the center of the mesh
    const vectorToCenter = new THREE.Vector3().subVectors(bboxCenter, vertex);

    // Calculate the dot product
    const dot = normal.dot(vectorToCenter);

    // If the dot product is positive, the normal is pointing inwards
    if (dot > 0) {
      invertedCount++;
    }
  }

  // You can adjust this threshold
  return invertedCount > positionAttribute.count / 2;
}

export function flipFaceNormals(geometry: THREE.BufferGeometry) {
  // Ensure geometry has an index buffer
  if (!geometry.index) {
    geometry = geometry.toNonIndexed(); // Convert to non-indexed if necessary
  }

  const indexAttr = geometry.index!;
  if (!indexAttr || indexAttr.count === 0) {
    console.error("flipFaceNormals: Invalid index attribute");
    return;
  }

  const indexArray = indexAttr.array;
  if (!indexArray || indexArray.length === 0) {
    console.error("flipFaceNormals: Invalid index array");
    return;
  }

  // Flip every triangle's winding order (swap the second and third index)
  for (let i = 0; i < indexArray.length; i += 3) {
    if (i + 2 < indexArray.length) {
      const tmp = indexArray[i + 1];
      indexArray[i + 1] = indexArray[i + 2];
      indexArray[i + 2] = tmp;
    }
  }

  indexAttr.needsUpdate = true;

  // Recalculate normals
  geometry.computeVertexNormals();
}

export const mapPointToLine = (linePoints: THREE.Vector3[], queryPoint: THREE.Vector3) => {
  let minDistanceSq = Infinity;
  let closestPoint = null;

  for (let i = 0; i < linePoints.length - 1; i++) {
    const p1 = linePoints[i];
    const p2 = linePoints[i + 1];

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const dz = p2.z - p1.z;
    const lengthSq = dx * dx + dy * dy + dz * dz;

    let t = 0;
    if (lengthSq > 0) {
      // Calculate t (dot product projection)
      t = ((queryPoint.x - p1.x) * dx + (queryPoint.y - p1.y) * dy + (queryPoint.z - p1.z) * dz) / lengthSq;
    }

    // Clamp t to the segment [0, 1]
    t = Math.max(0, Math.min(1, t));

    // Find the projected point (closest point on this segment)
    const projectedPoint = new THREE.Vector3(
      p1.x + t * dx,
      p1.y + t * dy,
      p1.z + t * dz
    );

    const currentDistanceSq = distanceSq(queryPoint, projectedPoint);

    // Check if this is the global minimum distance
    if (currentDistanceSq < minDistanceSq) {
      minDistanceSq = currentDistanceSq;
      closestPoint = projectedPoint;
    }
  }

  // Return the closest point and the actual (non-squared) distance
  return closestPoint;
};

function distanceSq(p1: THREE.Vector3, p2: THREE.Vector3) {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  const dz = p1.z - p2.z;
  return dx * dx + dy * dy + dz * dz;
}

export function lineSubdivide(points: THREE.Vector3[]) {
  if (points.length < 2) {
    return [...points];
  }

  const weights = { p: 0.3, q: 0.7 };

  for (let j = 0; j < 3; j++) {
    const newPoints = [points[0]];

    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];

      const p = new THREE.Vector3(
        (1 - weights.q) * p1.x + weights.q * p2.x,
        0,
        (1 - weights.q) * p1.z + weights.q * p2.z,
      );

      const q = new THREE.Vector3(
        weights.q * p1.x + (1 - weights.q) * p2.x,
        0,
        weights.q * p1.z + (1 - weights.q) * p2.z,
      );

      newPoints.push(q);
      newPoints.push(p);
    }
    points = [...newPoints, points[points.length - 1]];
  }

  return points;
}

export function saveGCodeToFile(content: string, filename: string, mimeType = 'text/plain') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  URL.revokeObjectURL(url); // Clean up the URL object
}

export function getPointYOnMesh(x: number, z: number, obj: THREE.Mesh) {
  const rayDirection = new THREE.Vector3(0, -1, 0); // Cast ray downward
  const worldVertex = new THREE.Vector3(x, 100, z); // Start ray above the mesh
  const raycaster = new THREE.Raycaster();
  raycaster.firstHitOnly = true;
  raycaster.set(worldVertex, rayDirection);
  const intersects = raycaster.intersectObject(obj, false);

  if (intersects.length > 0) return intersects[0].point.y;
  else return Number.NEGATIVE_INFINITY;
}

export function FindPointOnMesh(startPoint: THREE.Vector3, targetMesh: THREE.Mesh) {
  const direction = new THREE.Vector3(0, 100, 0)

  const raycaster = new THREE.Raycaster();
  raycaster.firstHitOnly = true;
  raycaster.set(startPoint, direction);
  raycaster.far = 100 + 0.1; // Add a small tolerance

  // IntersectObjects expects an array of Meshes
  const intersects = raycaster.intersectObject(targetMesh, true);

  if (intersects.length > 0) {
    // Return the point of the first intersection
    return intersects[0].point;
  }
  return null;
}

export function ShowCurveNormals(scene: THREE.Scene, curvePoints: THREE.Vector3[], curveNormals: THREE.Vector3[], length = 5, color = 0xff0000) {
  const group = new THREE.Group();
  group.name = "CurveNormals";

  for (let i = 0; i < curvePoints.length; i++) {
    const p = curvePoints[i];
    const n = curveNormals[i];

    // end point of the normal
    const end = p.clone().add(n.clone().multiplyScalar(length));

    const geometry = new THREE.BufferGeometry().setFromPoints([p, end]);
    const material = new THREE.LineBasicMaterial({ color });

    const line = new THREE.Line(geometry, material);
    group.add(line);
  }

  scene.add(group);

  return group;
}

export function DisposeMesh(child: THREE.Object3D) {
  if (child instanceof THREE.Mesh) {
    child.geometry?.dispose();

    if (Array.isArray(child.material)) {
      child.material.forEach(m => m.dispose());
    } else {
      child.material?.dispose();
    }
  }
}

export function GetInsoleBox(insole: THREE.Mesh): THREE.Box3 {
  const positionAttribute = insole.geometry.getAttribute('position');
  const selectedAttribute = insole.geometry.getAttribute('selected');

  if (!selectedAttribute) return new THREE.Box3;

  const selectedArray = selectedAttribute.array;
  let vertices = [];
  for (let i = 0; i < positionAttribute.count; i++) {
    const selectedValue = selectedArray[i];
    if (selectedValue > 0.9) {
      vertices.push(new THREE.Vector3(positionAttribute.getX(i), positionAttribute.getY(i), positionAttribute.getZ(i)));
    }
  }
  var bbox = new THREE.Box3().setFromPoints(vertices);

  return bbox;
}

export function extrudeAlongY(topLayer: THREE.Mesh, thickness: number, type: string = '3D', angle: number = Math.PI) {
  // Helper to create the other cap  
  function CreateBottomPoints(topLayer: THREE.Mesh, thickness: number, angle: number = 0) {
    topLayer.geometry.computeBoundsTree();

    const coverbox = new THREE.Box3().setFromObject(topLayer);
    const coversize = coverbox.getSize(new THREE.Vector3());
    const covercenter = coverbox.getCenter(new THREE.Vector3());

    const originalPositions = topLayer.geometry.getAttribute('position');
    const originalVertexCount = originalPositions.count;

    const newPositions = [];

    for (let i = 0; i < originalVertexCount; i++) {
      const vertex = new THREE.Vector3(originalPositions.getX(i), originalPositions.getY(i) + thickness, originalPositions.getZ(i));

      let cv = new THREE.Vector3().subVectors(covercenter, vertex);
      const cvLength = cv.length();
      if (cvLength > 0) {
        cv.divideScalar(cvLength).divideScalar(10);
      }

      // Raycasting to find intersection points
      const rayStart = new THREE.Vector3(vertex.x, 0, vertex.z); // Assuming 0 is a base plane

      let y = FindPointOnMesh(rayStart, topLayer);
      if (y === null) {
        const rayStartOffset = new THREE.Vector3(vertex.x + cv.x, 0, vertex.z + cv.z);
        y = FindPointOnMesh(rayStartOffset, topLayer);
      }

      if (y !== null) {
        // Raycasting for surrounding points
        let yBack = FindPointOnMesh(new THREE.Vector3(vertex.x, 0, vertex.z + thickness), topLayer);
        if (yBack === null) {
          yBack = FindPointOnMesh(new THREE.Vector3(vertex.x + cv.x, 0, vertex.z + cv.z + thickness), topLayer);
        }
        let yFront = FindPointOnMesh(new THREE.Vector3(vertex.x, 0, vertex.z - thickness), topLayer);
        if (yFront === null) {
          yFront = FindPointOnMesh(new THREE.Vector3(vertex.x + cv.x, 0, vertex.z + cv.z - thickness), topLayer);
        }

        let yRight = FindPointOnMesh(new THREE.Vector3(vertex.x - thickness, 0, vertex.z), topLayer);
        if (yRight === null) {
          yRight = FindPointOnMesh(new THREE.Vector3(vertex.x + cv.x - thickness, 0, vertex.z + cv.z), topLayer);
        }

        let yLeft = FindPointOnMesh(new THREE.Vector3(vertex.x + thickness, 0, vertex.z), topLayer);
        if (yLeft === null) {
          yLeft = FindPointOnMesh(new THREE.Vector3(vertex.x + cv.x + thickness, 0, vertex.z + cv.z), topLayer);
        }

        // Handle null cases 
        if (yRight === null) yRight = new THREE.Vector3(0, 100, 0);
        if (yBack === null) yBack = new THREE.Vector3(0, 100, 0);
        if (yLeft === null) yLeft = new THREE.Vector3(0, 100, 0);
        if (yFront === null) yFront = new THREE.Vector3(0, 100, 0);
        if (y === null) y = new THREE.Vector3(0, 100, 0); // This case should ideally be caught earlier

        // Adjust Y values of surrounding points based on 'v'
        yBack.y = yBack.y - thickness;
        yFront.y = yFront.y - thickness;
        yRight.y = yRight.y - thickness;
        yLeft.y = yLeft.y - thickness;

        // Complex conditional adjustments 
        if (yFront.y !== 100 - thickness && yBack.y !== 100 - thickness) {
          if (yFront.y > yBack.y) {
            yBack.y -= (yFront.y - yBack.y);
          }
          if (yBack.y > yFront.y) {
            yFront.y -= (yBack.y - yFront.y) * (y.z - coverbox.min.z) / (coversize.z);
          }
        }

        // Calculate min y in area
        let minyinarea = Math.min(yBack.y, yRight.y, yLeft.y, y.y, yFront.y);

        // Conditional adjustment based on Z coordinate and BoxMax.Z
        const BackThrZ = coverbox.min.z + coversize.z * Math.cos(angle);
        if (y.z > BackThrZ) {
          minyinarea = (minyinarea - y.y) * (coverbox.max.z - y.z) / (coverbox.max.z - BackThrZ) + y.y;
        }

        // Update the vertex's Y coordinate
        newPositions.push(vertex.x, minyinarea, vertex.z);
      } else {
        newPositions.push(vertex.x, vertex.y, vertex.z); // If condition not met, keep original vertex
      }
    }

    return newPositions;
  }

  // Helper to get edges from faces
  function getEdgesFromGeometry(geometry: THREE.BufferGeometry, type: string = '3D') {
    const edges = new Set<string>();
    const index = geometry.index;
    if (!index) {
      // Handle non-indexed geometry (each face is a separate set of 3 vertices)
      console.warn("Non-indexed geometry detected. Edge detection might be less efficient.");

      const positions = geometry.attributes.position;
      for (let i = 0; i < positions.count; i += 3) {
        const v1 = i;
        const v2 = i + 1;
        const v3 = i + 2;
        addEdge(edges, v1, v2, type);
        addEdge(edges, v2, v3, type);
        addEdge(edges, v3, v1, type);
      }
    } else {
      // Indexed geometry
      for (let i = 0; i < index.count; i += 3) {
        const v1 = index.getX(i);
        const v2 = index.getX(i + 1);
        const v3 = index.getX(i + 2);
        addEdge(edges, v1, v2, type);
        addEdge(edges, v2, v3, type);
        addEdge(edges, v3, v1, type);
      }
    }
    return edges;
  }

  // Helper to add an edge, ensuring consistent order for detection
  function addEdge(edgesSet: Set<string>, vA: number, vB: number, type: string = '3D') {
    if (type === 'Plane') {
      edgesSet.add(`${vA}-${vB}`);
      edgesSet.add(`${vB}-${vA}`);
    } else {
      if (edgesSet.has(`${vA}-${vB}`) || edgesSet.has(`${vB}-${vA}`)) {
        edgesSet.delete(`${vA}-${vB}`); // If found, it's an internal edge, remove it
        edgesSet.delete(`${vB}-${vA}`);
      } else {
        edgesSet.add(`${vA}-${vB}`); // If not found, it's potentially an outer edge
      }
    }
  }

  // Helper to parse edges back into pairs
  function parseEdges(edgesSet: Set<string>) {
    const edgePairs: number[][] = [];
    edgesSet.forEach((key: string) => {
      const [vA, vB] = key.split('-').map(Number);
      edgePairs.push([vA, vB]);
    });
    return edgePairs;
  }

  let cover = new THREE.Mesh(topLayer.geometry.clone());
  // Bake only X/Z scale from topLayer into cover geometry so size is preserved
  cover.geometry.scale(topLayer.scale.x, 1, topLayer.scale.z);

  // Clear duplicate vertices
  cover.geometry = clearDuplicateVertices(cover.geometry);

  // Get original positions and indices
  const originalPositions = cover.geometry.getAttribute('position');
  const originalIndex = cover.geometry.getIndex();

  if (!originalPositions || !originalIndex) {
    console.error("Geometry must have position and index attributes for extrusion.");
    return;
  }

  const originalVertexCount = originalPositions.count;
  const originalFaceCount = originalIndex.count / 3;

  // Create a new BufferGeometry to build the extruded shape
  const extrudedGeometry = new THREE.BufferGeometry();
  let newPositions = [];
  const newNormals = [];
  const newIndices = [];

  // --- Step 1: Add original (top) vertices and faces ---
  // Duplicate original vertices to allow for proper normal calculation later
  for (let i = 0; i < originalVertexCount; i++) {
    newPositions.push(originalPositions.getX(i), originalPositions.getY(i), originalPositions.getZ(i));
    newNormals.push(0, 0, 0, 0, 0, 0); // Placeholder, will be computed
  }
  for (let i = 0; i < originalIndex.count; i++) {
    newIndices.push(originalIndex.getX(i));
  }

  // --- Step 2: Add extruded (bottom) vertices ---
  // Extruded vertices start at `originalVertexCount` in the new array
  const extrudedStartIndex = originalVertexCount;

  if (type == 'relative')
    newPositions = newPositions.concat(CreateBottomPoints(cover, thickness, angle));
  else
    for (let i = 0; i < originalVertexCount; i++) {
      newPositions.push(
        originalPositions.getX(i),
        originalPositions.getY(i) + thickness,
        originalPositions.getZ(i));
    }

  // --- Step 3: Create side faces ---
  // This is the trickiest part for an arbitrary mesh.
  // We need to find the "boundary" edges of the original mesh.
  // A simple way is to count how many faces share an edge. If an edge is shared by only one face, it's a boundary edge.
  const boundaryEdges = parseEdges(getEdgesFromGeometry(cover.geometry, type));

  boundaryEdges.forEach(edge => {
    const vA_orig = edge[0]; // Index of first vertex of original edge
    const vB_orig = edge[1]; // Index of second vertex of original edge

    const vA_extr = vA_orig + extrudedStartIndex; // Index of first vertex of extruded edge
    const vB_extr = vB_orig + extrudedStartIndex; // Index of second vertex of extruded edge

    // Create two triangles for the quad side face:
    // Triangle 1: vA_orig, vB_orig, vB_extr
    newIndices.push(vB_extr, vB_orig, vA_orig);
    // Triangle 2: vA_orig, vB_extr, vA_extr
    newIndices.push(vA_extr, vB_extr, vA_orig);

    //from gcodegenerator
    // // Triangle 1: vA_orig, vB_orig, vB_extr
    // newIndices.push(vA_orig, vB_orig, vB_extr);
    // // Triangle 2: vA_orig, vB_extr, vA_extr
    // newIndices.push(vA_orig, vB_extr, vA_extr);
  });

  // --- Step 4: Create bottom cap faces ---
  // This assumes the original topLayer has a "bottom" (meaning, it's a 2D plane with faces pointing one way).
  // If the original mesh is meant to be a solid with a top and bottom, this logic needs adjustment.
  // For a true "planar extrusion", we create a reversed copy of the original top faces
  // using the extruded vertices.
  for (let i = 0; i < originalFaceCount; i++) {
    const idx1 = originalIndex.getX(i * 3);
    const idx2 = originalIndex.getX(i * 3 + 1);
    const idx3 = originalIndex.getX(i * 3 + 2);

    // Add new indices for the bottom cap, pointing outwards
    // Note: Order might need to be reversed depending on desired normal direction for bottom cap
    newIndices.push(idx1 + extrudedStartIndex, idx3 + extrudedStartIndex, idx2 + extrudedStartIndex); // Reversed winding order for bottom
  }

  // Set attributes for the new geometry
  extrudedGeometry.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));
  extrudedGeometry.setAttribute('normal', new THREE.Float32BufferAttribute(newNormals, 3));
  extrudedGeometry.setIndex(new THREE.Uint32BufferAttribute(newIndices, 1));

  // Update the mesh's geometry
  cover.geometry.dispose(); // Dispose old geometry to free memory
  cover.geometry = extrudedGeometry;

  // Recompute normals and bounding box for the new, extruded mesh
  cover.geometry.attributes.position.needsUpdate = true;
  cover.geometry.computeVertexNormals();
  cover.geometry.computeBoundingBox();

  // Validate the generated geometry before returning
  if (!validateGeometry(cover.geometry)) {
    console.error("Generated cover mesh is invalid");
    return null;
  }
  return cover;
}

export function isBoxInside(inner: THREE.Box3, outer: THREE.Box3): boolean {
  return (
    inner.min.x >= outer.min.x &&
    inner.max.x <= outer.max.x &&
    inner.min.y >= outer.min.y &&
    inner.max.y <= outer.max.y &&
    inner.min.z >= outer.min.z &&
    inner.max.z <= outer.max.z
  );
}

export function fitInside(innerMesh: THREE.Object3D, outerMesh: THREE.Object3D) {
  const outer = new THREE.Box3().setFromObject(outerMesh);
  const inner = new THREE.Box3().setFromObject(innerMesh);

  const move = new THREE.Vector3();

  // X
  if (inner.min.x < outer.min.x)
    move.x = outer.min.x - inner.min.x;
  else if (inner.max.x > outer.max.x)
    move.x = outer.max.x - inner.max.x;

  // Z
  if (inner.min.z < outer.min.z)
    move.z = outer.min.z - inner.min.z;
  else if (inner.max.z > outer.max.z)
    move.z = outer.max.z - inner.max.z;

  innerMesh.position.add(move);
  innerMesh.updateMatrixWorld(true);
}

// Function to apply subdivision to a geometry
export function applySubdivision(geometry: THREE.BufferGeometry, level: number): THREE.BufferGeometry {
  if (level <= 0) return geometry;

  // console.log(`Applying subdivision level ${level} to geometry`);

  // Make sure the geometry has the required attributes for subdivision
  if (!geometry.attributes.position || !geometry.attributes.normal) {
    console.error('Geometry missing required attributes for subdivision');
    return geometry;
  }

  // Define subdivision parameters
  const params = {
    split: false,           // optional, default: true
    uvSmooth: true,        // enable UV smoothing
    preserveEdges: false,  // don't preserve edges for smoother result
    flatOnly: false,       // don't use flat shading
    maxTriangles: Infinity // optional, default: Infinity
  };

  // Apply subdivision with parameters
  try {
    // console.log('Applying subdivision with parameters:', params);
    const subdividedGeometry = LoopSubdivision.modify(geometry as any, level, params);

    // Ensure normals are smoothed
    //subdividedGeometry.computeVertexNormals();

    // console.log('Subdivision completed successfully');
    return subdividedGeometry;
  } catch (error) {
    console.error('Error during subdivision:', error);
    return geometry; // Return original geometry if subdivision fails
  }
};


export function prepareGeometry(geometry: THREE.BufferGeometry) {
  const geom = geometry.clone();
  if (!geom.index) {
    geom.setIndex(Array.from({ length: geom.attributes.position.count }, (_, i) => i));
  }

  if (areNormalsInverted(geom)) {
    flipFaceNormals(geom);
  }

  geom.computeVertexNormals();
  geom.computeBoundingBox();
  return geom;
}
